/**
 * One address, written two ways. R-303.
 *
 * ── WHAT HAPPENED ───────────────────────────────────────────────────────────
 *
 * The order screen rendered `line1`, `suburb`, `state` and `postcode`, which
 * is the shape this system's own checkout writes and the shape `Address`
 * declares. The 12,656 orders imported from Shopify hold Shopify's shape:
 *
 *   { name, address1, address2, city, province, zip, country, phone }
 *
 * So the "Shipping to" card was EMPTY on every historical order. Not missing,
 * not an error: a heading with a blank space under it, on the screen somebody
 * opens to find out where to send a repeat order. The data was there the whole
 * time under different keys.
 *
 * Found by opening one order on the live site and looking at it, which is the
 * only way this class of defect is ever found: every test passed, because
 * every test seeds the shape the code reads.
 *
 * ── WHY NOT REWRITE THE ROWS ────────────────────────────────────────────────
 *
 * `address1` and `city` are what Shopify sent and what Shopify still holds. A
 * migration that renamed them would make this system disagree with the system
 * the orders came from, for the sake of saving one function. And 601 of those
 * orders carry `{}`: no address at all, which is a fact about a pickup or a
 * cancelled order and not a shape problem.
 *
 * So the reading is normalised in one place, and a row with nothing in it says
 * so in words.
 */

/** Either shape, and neither field list is complete. */
export interface AddressLike {
  // This system's own.
  line1?: string | null;
  line2?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  // Shopify's, as the import wrote it.
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
}

export interface ReadableAddress {
  /** The person or company, when the source carried one. */
  name: string | null;
  /** Street lines, in order, with the empty ones dropped. */
  street: string[];
  /** "Palmerston North, Manawatu-Wanganui 4410", already joined. */
  locality: string;
  country: string | null;
  phone: string | null;
  /** False when there is nothing to show, so a caller can say so. */
  hasAnything: boolean;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function readAddress(raw: AddressLike | null | undefined): ReadableAddress {
  const a = raw ?? {};

  const street = [
    text(a.line1) || text(a.address1),
    text(a.line2) || text(a.address2),
  ].filter(Boolean);

  /**
   * Suburb, state and postcode in one line, comma between the place and the
   * region and a space before the number, which is how an Australian address
   * is written and close enough to how everywhere else writes it.
   */
  const place = text(a.suburb) || text(a.city);
  const region = text(a.state) || text(a.province);
  const code = text(a.postcode) || text(a.zip);
  const locality = [[place, region].filter(Boolean).join(', '), code]
    .filter(Boolean)
    .join(' ');

  const name = text(a.name) || null;
  const country = text(a.country) || null;
  const phone = text(a.phone) || null;

  return {
    name,
    street,
    locality,
    country,
    phone,
    hasAnything: Boolean(name || street.length || locality || country || phone),
  };
}

/* -------------------------------------------------------------------------- */
/* The one address this system writes                                         */
/* -------------------------------------------------------------------------- */

/**
 * The shape a person types on a customer (spec 016 FR-1602) and a supplier
 * (spec 013 FR-1303), and the shape an order's address already uses: `line1`
 * to `postcode` are the keys of `Address` in `src/lib/types.ts` and of the
 * order form, so `readAddress` reads all three without a second rule.
 *
 * Every field is a string, empty when not known. A form holds this, and
 * `parseAddress` turns it into what is stored: null when nothing was typed.
 */
export interface PostalAddress {
  line1: string;
  line2: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

export const ADDRESS_KEYS = ['line1', 'line2', 'suburb', 'state', 'postcode', 'country'] as const;

/** The states and territories, for the form's list. Other text is accepted. */
export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const;

const LIMITS: Record<(typeof ADDRESS_KEYS)[number], number> = {
  line1: 200, line2: 200, suburb: 120, state: 60, postcode: 20, country: 80,
};

export function blankAddress(): PostalAddress {
  return { line1: '', line2: '', suburb: '', state: '', postcode: '', country: 'Australia' };
}

/**
 * Either stored shape, as the form's fields. A Shopify address opens with its
 * `address1` in Street and its `city` in Suburb, and saves in this shape.
 */
export function toPostalAddress(raw: AddressLike | null | undefined): PostalAddress {
  const a = raw ?? {};
  return {
    line1: text(a.line1) || text(a.address1),
    line2: text(a.line2) || text(a.address2),
    suburb: text(a.suburb) || text(a.city),
    state: text(a.state) || text(a.province),
    postcode: text(a.postcode) || text(a.zip),
    country: text(a.country) || 'Australia',
  };
}

/** True when a person typed nothing but the country the form starts with. */
export function isBlankAddress(a: PostalAddress): boolean {
  return !a.line1 && !a.line2 && !a.suburb && !a.state && !a.postcode;
}

/**
 * What a caller sent, checked. Null, undefined or a blank form is no address.
 *
 * A street with no suburb is refused, because a courier cannot use it, and an
 * Australian postcode is four digits. A message, not a throw: a person typed it.
 */
export function parseAddress(
  raw: unknown,
  label: string,
): { ok: true; value: PostalAddress | null } | { ok: false; message: string } {
  if (raw === null || raw === undefined || raw === '') return { ok: true, value: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, message: `The ${label} is not an address.` };
  }
  const r = raw as Record<string, unknown>;
  const out = blankAddress();
  for (const k of ADDRESS_KEYS) {
    const v = r[k];
    if (v === undefined || v === null) continue;
    if (typeof v !== 'string') return { ok: false, message: `The ${label} has a ${k} that is not text.` };
    const t = v.trim();
    if (t.length > LIMITS[k]) {
      return { ok: false, message: `The ${label} has a ${k} longer than ${LIMITS[k]} characters.` };
    }
    out[k] = t;
  }
  if (!out.country) out.country = 'Australia';
  if (isBlankAddress(out)) return { ok: true, value: null };

  if (!out.line1) return { ok: false, message: `Type the street of the ${label}.` };
  if (!out.suburb) return { ok: false, message: `Type the suburb of the ${label}.` };
  if (out.country.toLowerCase() === 'australia' && out.postcode && !/^\d{4}$/.test(out.postcode)) {
    return { ok: false, message: `An Australian postcode is four digits. The ${label} has "${out.postcode}".` };
  }
  return { ok: true, value: out };
}
